import React from 'react';
import { createPortal } from 'react-dom';

import { useTheme } from '@mui/material';
import { css, keyframes } from '@emotion/css';

import { useBypassTrapActive } from './bypassTrapStore';

// STYLES ---------------------------------------------------------------- //

// Slide the oversized gradient across the screen so the rainbow bands travel.
const drift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// Cycle the whole spectrum on top of the drift so the colors are always shifting.
const spin = keyframes`
  from { filter: hue-rotate(0deg); }
  to { filter: hue-rotate(360deg); }
`;

// COMPONENT ---------------------------------------------------------------- //

/**
 * Full-screen animated overlay for the disabled-bypass-trap effect. Renders nothing until
 * the trap is triggered, then paints a dynamically shifting rainbow film over the entire
 * viewport.
 *
 * Portaled to `document.body` and pinned just below the toast layer so it sits on top of
 * everything (including modals/drawers) except toasts and tooltips. It captures pointer
 * events, so it blocks the user from clicking through to the app underneath — the toast and
 * tooltips remain reachable since they render above it. Mount once, near the app root.
 */
const BypassTrapOverlay: React.FC = () => {
  const active = useBypassTrapActive();
  const theme = useTheme();

  if (!active) return null;

  const overlay = css`
    position: fixed;
    inset: 0;
    z-index: ${(theme.zIndex?.snackbar ?? 1400) - 1};
    pointer-events: auto;
    cursor: not-allowed;
    opacity: 0.4;
    background: linear-gradient(
      90deg,
      #ff0040,
      #ff8c00,
      #ffee00,
      #33ff00,
      #00ffee,
      #3366ff,
      #cc00ff,
      #ff0040
    );
    background-size: 400% 400%;
    animation: ${drift} 6s linear infinite, ${spin} 8s linear infinite;
  `;

  return createPortal(<div aria-hidden className={overlay} />, document.body);
};

// EXPORT ---------------------------------------------------------------- //

export default BypassTrapOverlay;
